// Generated by Claude (Claude Code): https://claude.ai/code/session_01XjQBvV5vxzvd6TGZribDqy
//
// Review queue for changes Claude proposed through the staff MCP connector.
// Nothing Claude drafts reaches a submitter or the public board until a staff
// member applies it here.
import { useState } from "react";
import { Link } from "wouter";
import { format } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";
import {
  getGetReportQueryKey,
  getListDraftsQueryKey,
  getListReportsQueryKey,
  useApplyDraft,
  useDiscardDraft,
  useListCategories,
  useListDrafts,
  useListStatuses,
  type Draft,
} from "@workspace/api-client-react";
import StaffLayout from "@/components/StaffLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Bot, Check, Mail, X } from "lucide-react";

type Tab = "pending" | "failed";

const KIND_LABEL: Record<Draft["kind"], string> = {
  comment: "Reply",
  update: "Update",
  link_duplicate: "Mark duplicate",
  unlink_duplicate: "Remove duplicate link",
};

function useLabels() {
  const { data: statuses } = useListStatuses();
  const { data: categories } = useListCategories();
  const status = (id: number | null | undefined) =>
    id == null ? "None" : statuses?.find((s) => s.id === id)?.label ?? `#${id}`;
  const category = (id: number | null | undefined) =>
    id == null ? "None" : categories?.find((c) => c.id === id)?.name ?? `#${id}`;
  return { status, category };
}

function UpdateDiff({ draft }: { draft: Draft }) {
  const labels = useLabels();
  const p = draft.payload;
  const rows: Array<[string, string | null, string]> = [];
  if (p.statusId !== undefined) rows.push(["Status", labels.status(draft.currentStatusId), labels.status(p.statusId)]);
  if (p.categoryId !== undefined) rows.push(["Category", labels.category(draft.currentCategoryId), labels.category(p.categoryId)]);
  if (p.verified !== undefined) rows.push(["Verified", null, p.verified ? "Yes" : "No"]);
  if (p.hasWorkaround !== undefined) rows.push(["Workaround", null, p.hasWorkaround ? "Yes" : "No"]);
  if (p.title !== undefined) rows.push(["Title", draft.reportTitle ?? null, p.title]);
  if (p.description !== undefined) rows.push(["Description", null, p.description]);
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
      {rows.map(([field, from, to]) => (
        <div key={field} className="contents">
          <dt className="font-medium text-slate-600 dark:text-slate-300">{field}</dt>
          <dd data-testid={`draft-${draft.id}-change-${field.toLowerCase()}`}>
            {from !== null && <span className="text-slate-500 line-through mr-2">{from}</span>}
            <span className="font-medium">{to}</span>
          </dd>
        </div>
      ))}
    </dl>
  );
}

function DraftCard({ draft }: { draft: Draft }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [body, setBody] = useState(draft.payload.body ?? "");

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getListDraftsQueryKey({ status: "pending" }) });
    queryClient.invalidateQueries({ queryKey: getListDraftsQueryKey({ status: "failed" }) });
    queryClient.invalidateQueries({ queryKey: getListReportsQueryKey() });
    queryClient.invalidateQueries({ queryKey: getGetReportQueryKey(draft.reportId) });
  };

  const apply = useApplyDraft({
    mutation: {
      onSuccess: (result) => {
        invalidate();
        if (result.status === "failed") {
          toast({
            title: `Draft #${draft.id} could not be applied`,
            description: result.resultMessage ?? undefined,
            variant: "destructive",
          });
        } else if (result.resultMessage) {
          // Applied (the comment/change is saved) but the submitter email failed.
          toast({
            title: draft.kind === "comment" ? "Reply saved, email failed" : "Change saved, email failed",
            description: result.resultMessage,
            variant: "destructive",
          });
        } else if (draft.kind === "comment" && !draft.emailsSubmitter) {
          // Staff-created issues have no submitter email; the reply is only posted.
          toast({
            title: "Reply posted",
            description: "This issue has no submitter email, so nobody was emailed.",
          });
        } else {
          toast({ title: draft.kind === "comment" ? "Reply sent" : "Change applied" });
        }
      },
      onError: () => {
        invalidate();
        toast({ title: "Couldn't apply this draft", description: "It may already have been handled.", variant: "destructive" });
      },
    },
  });
  const discard = useDiscardDraft({
    mutation: {
      onSuccess: () => {
        invalidate();
        toast({ title: "Draft discarded" });
      },
      onError: () => {
        invalidate();
        toast({ title: "Couldn't discard this draft", variant: "destructive" });
      },
    },
  });

  const busy = apply.isPending || discard.isPending;
  const edited = draft.kind === "comment" && body.trim() !== (draft.payload.body ?? "");
  const canApply = draft.kind !== "comment" || body.trim().length > 0;

  return (
    <Card data-testid={`draft-card-${draft.id}`}>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary">{KIND_LABEL[draft.kind]}</Badge>
          {draft.emailsSubmitter && (
            <Badge variant="outline" className="gap-1" data-testid={`draft-${draft.id}-emails`}>
              <Mail className="w-3 h-3" /> Emails submitter
            </Badge>
          )}
          <span className="text-xs text-slate-500">
            Drafted {draft.createdByName ? `for ${draft.createdByName} ` : ""}
            {format(new Date(draft.createdAt), "MMM d, h:mm a")}
          </span>
        </div>
        <CardTitle className="text-base">
          <Link href={`/staff?report=${draft.reportId}`} className="hover:underline">
            #{draft.reportId} {draft.reportTitle ?? "(untitled)"}
          </Link>
        </CardTitle>
        <CardDescription className="line-clamp-2">{draft.reportDescription}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {draft.rationale && (
          <p className="text-sm text-slate-600 dark:text-slate-300 flex gap-2">
            <Bot className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{draft.rationale}</span>
          </p>
        )}

        {draft.kind === "update" && <UpdateDiff draft={draft} />}
        {draft.kind === "comment" && (
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={Math.min(10, Math.max(3, body.split("\n").length + 1))}
            aria-label={`Reply text for draft ${draft.id}`}
            data-testid={`draft-${draft.id}-body`}
          />
        )}
        {draft.kind === "link_duplicate" && (
          <p className="text-sm">
            Mark as a duplicate of{" "}
            <Link href={`/staff?report=${draft.payload.primaryId}`} className="font-medium hover:underline">
              #{draft.payload.primaryId} {draft.primaryTitle ?? ""}
            </Link>
            . It will be hidden from the public board.
          </p>
        )}
        {draft.kind === "unlink_duplicate" && (
          <p className="text-sm">
            Remove the duplicate link
            {draft.currentDuplicateOfId ? ` to #${draft.currentDuplicateOfId}` : ""}. It will show on the public
            board again.
          </p>
        )}

        {draft.status === "failed" && draft.resultMessage && (
          <p className="text-sm text-destructive" data-testid={`draft-${draft.id}-error`}>
            Last attempt failed: {draft.resultMessage}
          </p>
        )}

        <div className="flex gap-2 pt-1">
          <Button
            size="sm"
            disabled={busy || !canApply}
            onClick={() =>
              apply.mutate({ id: draft.id, data: edited ? { body: body.trim() } : undefined })
            }
            data-testid={`draft-${draft.id}-apply`}
          >
            <Check className="w-4 h-4 mr-1" />
            {draft.kind === "comment" ? (draft.emailsSubmitter ? "Send reply" : "Post reply") : "Apply"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => discard.mutate({ id: draft.id })}
            data-testid={`draft-${draft.id}-discard`}
          >
            <X className="w-4 h-4 mr-1" /> Discard
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function StaffDraftsPage() {
  const [tab, setTab] = useState<Tab>("pending");
  const { data: drafts, isLoading } = useListDrafts({ status: tab });

  return (
    <StaffLayout>
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold" data-testid="staff-drafts-title">Drafts from Claude</h1>
          <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
            Changes Claude proposed through the staff connector. Nothing here has been sent or changed yet.
            Applying runs it as you, including any email to the submitter.
          </p>
        </div>

        <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
          <TabsList>
            <TabsTrigger value="pending">Pending</TabsTrigger>
            <TabsTrigger value="failed">Failed</TabsTrigger>
          </TabsList>
        </Tabs>

        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : !drafts || drafts.length === 0 ? (
          <p className="text-sm text-slate-500 py-10 text-center" data-testid="drafts-empty">
            {tab === "pending" ? "No drafts waiting for review." : "No failed drafts."}
          </p>
        ) : (
          <div className="space-y-4">
            {drafts.map((d) => (
              <DraftCard key={d.id} draft={d} />
            ))}
          </div>
        )}
      </div>
    </StaffLayout>
  );
}
